import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { RefreshCw as RefreshIcon } from 'lucide-react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import SimpleBarChart from '../analytics/SimpleBarChart';
import TimeSeriesChart from '../analytics/TimeSeriesChart';

interface DailyStats {
  date: string;
  pageViews: {
    total: number;
    unique: number;
    topPages: Array<{ page: string; views: number; avgTime: number }>;
  };
  sessions: {
    total: number;
    averageDuration: number;
    bounceRate: number;
    mobileShare: number;
  };
  registrations: {
    started: number;
    completed: number;
    byEvent: Record<string, number>;
    conversionRate: number;
  };
  financial: {
    totalIncome: number;
    paymentsMade: number;
    outstandingAmount: number;
    refunds: number;
  };
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, trend, color = 'primary' }) => {
  const getTrendColor = (trend: number | undefined) => {
    if (!trend) return 'text.secondary';
    return trend > 0 ? 'success.main' : 'error.main';
  };

  const getTrendIcon = (trend: number | undefined) => {
    if (!trend) return '';
    return trend > 0 ? '↑' : '↓';
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" component="div" color={color + '.main'}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend !== undefined && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color={getTrendColor(trend)}>
              {getTrendIcon(trend)} {Math.abs(trend)}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const AnalyticsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(7); // Last 7 days
  const [currentStats, setCurrentStats] = useState<DailyStats | null>(null);
  const [previousStats, setPreviousStats] = useState<DailyStats | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<Array<{ date: string; pageViews: number; sessions: number; registrations: number }>>([]);
  const [hourlyActivity, setHourlyActivity] = useState<Array<{ hour: number; pageViews: number; sessions: number }>>([]);
  const [dailyActivity, setDailyActivity] = useState<Array<{ day: number; dayName: string; pageViews: number; sessions: number }>>([]);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [refreshInterval, setRefreshInterval] = useState<number>(30000); // 30 seconds default
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [excludeAdmin, setExcludeAdmin] = useState<boolean>(true);

  const fetchStats = async (days: number) => {
    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      const currentStart = startOfDay(subDays(now, days - 1));
      const currentEnd = endOfDay(now);

      // Fetch real analytics data from Firestore
      const analyticsData = await fetchRealAnalyticsData(currentStart, currentEnd);
      
      setCurrentStats(analyticsData);
      
      // Fetch previous period data for trends
      const previousStart = startOfDay(subDays(currentStart, days));
      const previousEnd = endOfDay(subDays(now, days));
      const previousData = await fetchRealAnalyticsData(previousStart, previousEnd);
      setPreviousStats(previousData);

      // Fetch time-series data for charts
      const timeSeries = await fetchTimeSeriesData(currentStart, currentEnd);
      setTimeSeriesData(timeSeries);

      // Calculate hourly and daily activity patterns
      const hourlyPattern = await calculateHourlyActivity(currentStart, currentEnd);
      setHourlyActivity(hourlyPattern);
      
      const dailyPattern = await calculateDailyActivity(currentStart, currentEnd);
      setDailyActivity(dailyPattern);

      setLastRefresh(new Date());

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch real analytics data from Firestore
  const fetchRealAnalyticsData = async (startDate: Date, endDate: Date): Promise<DailyStats> => {
    const stats: DailyStats = {
      date: format(new Date(), 'yyyy-MM-dd'),
      pageViews: {
        total: 0,
        unique: 0,
        topPages: []
      },
      sessions: {
        total: 0,
        averageDuration: 0,
        bounceRate: 0,
        mobileShare: 0
      },
      registrations: {
        started: 0,
        completed: 0,
        byEvent: {},
        conversionRate: 0
      },
      financial: {
        totalIncome: 0,
        paymentsMade: 0,
        outstandingAmount: 0,
        refunds: 0
      }
    };

    const pageViewsByPage = new Map<string, { views: number; totalTime: number; uniqueSessions: Set<string> }>();
    const allPageViewSessions = new Set<string>(); // Global unique sessions across all pages
    const sessionsByDuration: number[] = [];
    let mobileSessions = 0;
    let totalSessions = 0;
    const registrationStarts = new Set<string>();
    const registrationCompletions = new Set<string>();
    const registrationsByEvent = new Map<string, number>();

    // Track which dates have raw events so we don't double-count with aggregates
    const datesWithRawEvents = new Set<string>();

    // Fetch raw events for each day in the range
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    for (let i = 0; i <= daysDiff; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      
      try {
        const eventsCollectionRef = collection(db, 'analytics', 'events', dateStr);
        const eventsSnapshot = await getDocs(eventsCollectionRef);
        
        if (!eventsSnapshot.empty) {
          datesWithRawEvents.add(dateStr);
        }
        
        // eslint-disable-next-line no-loop-func
        eventsSnapshot.forEach(doc => {
          const event = doc.data() as any;
          
          // Skip admin events if filter is enabled
          if (excludeAdmin && event.isAdmin === true) return;
          
          // Track page views
          if (event.type === 'page_view' && event.data.page) {
            stats.pageViews.total++;
            
            if (!pageViewsByPage.has(event.data.page)) {
              pageViewsByPage.set(event.data.page, { 
                views: 0, 
                totalTime: 0, 
                uniqueSessions: new Set() 
              });
            }
            
            const pageData = pageViewsByPage.get(event.data.page)!;
            pageData.views++;
            pageData.uniqueSessions.add(event.sessionId);
            allPageViewSessions.add(event.sessionId);
          }
          
          // Track sessions
          if (event.type === 'session_event') {
            if (event.data.action === 'session_start') {
              totalSessions++;
              if (event.data.isMobile) {
                mobileSessions++;
              }
            } else if (event.data.action === 'session_end' && event.data.duration) {
              sessionsByDuration.push(event.data.duration);
            }
          }
          
          // Track registrations
          if (event.type === 'registration_event') {
            if (event.data.action === 'registration_start') {
              registrationStarts.add(event.sessionId);
            } else if (event.data.action === 'registration_complete') {
              registrationCompletions.add(event.sessionId);
              if (event.data.eventId) {
                registrationsByEvent.set(
                  event.data.eventId, 
                  (registrationsByEvent.get(event.data.eventId) || 0) + 1
                );
              }
            }
          }
        });
      } catch (error) {
        console.warn(`No analytics data for date: ${dateStr}`);
      }
    }

    // Fetch aggregated data for dates without raw events
    try {
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      const aggPeriodsRef = collection(db, 'analytics', 'aggregates', 'periods');
      const aggSnapshot = await getDocs(aggPeriodsRef);

      aggSnapshot.forEach(doc => {
        const agg = doc.data() as any;
        if (!agg.startDate || !agg.endDate) return;

        // Check if this aggregate overlaps with our query range
        if (agg.endDate < startStr || agg.startDate > endStr) return;

        // Check if the aggregate's source dates are already covered by raw events
        const sourceDates: string[] = agg.sourceDates || [];
        const alreadyCovered = sourceDates.length > 0 && sourceDates.every((d: string) => datesWithRawEvents.has(d));
        if (alreadyCovered) return;

        // Apply admin filter: use non-admin counts when excluding admin
        const pvCount = excludeAdmin ? (agg.pageViews - (agg.adminPageViews || 0)) : agg.pageViews;
        const sessCount = excludeAdmin ? (agg.sessions - (agg.adminSessions || 0)) : agg.sessions;

        stats.pageViews.total += Math.max(0, pvCount || 0);
        totalSessions += Math.max(0, sessCount || 0);
        stats.registrations.started += agg.registrationStarts || 0;
        stats.registrations.completed += agg.registrationCompletions || 0;
        mobileSessions += agg.mobileSessions || 0;

        if (agg.totalSessionDurationMs && agg.sessionCount) {
          sessionsByDuration.push(agg.totalSessionDurationMs / agg.sessionCount);
        }

        // Merge top pages from aggregate
        if (agg.topPages) {
          for (const [page, pageStats] of Object.entries(agg.topPages as Record<string, any>)) {
            if (!pageViewsByPage.has(page)) {
              pageViewsByPage.set(page, { views: 0, totalTime: 0, uniqueSessions: new Set() });
            }
            const pd = pageViewsByPage.get(page)!;
            pd.views += pageStats.views || 0;
          }
        }

        // Merge registration by event
        if (agg.registrationsByEvent) {
          for (const [eventId, count] of Object.entries(agg.registrationsByEvent as Record<string, number>)) {
            registrationsByEvent.set(eventId, (registrationsByEvent.get(eventId) || 0) + count);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to fetch aggregate analytics data:', error);
    }

    // Calculate page view stats
    stats.pageViews.unique = allPageViewSessions.size;
    
    stats.pageViews.topPages = Array.from(pageViewsByPage.entries())
      .map(([page, data]) => ({
        page,
        views: data.views,
        avgTime: data.totalTime / Math.max(data.views, 1)
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    // Calculate session stats
    stats.sessions.total = totalSessions;
    // Duration is stored in milliseconds, convert to seconds for display
    stats.sessions.averageDuration = sessionsByDuration.length > 0 
      ? (sessionsByDuration.reduce((sum, duration) => sum + duration, 0) / sessionsByDuration.length) / 1000
      : 0;
    stats.sessions.mobileShare = totalSessions > 0 ? mobileSessions / totalSessions : 0;
    stats.sessions.bounceRate = 0; // TODO: Calculate bounce rate

    // Calculate registration stats
    stats.registrations.started = registrationStarts.size;
    stats.registrations.completed = registrationCompletions.size;
    stats.registrations.byEvent = Object.fromEntries(registrationsByEvent);
    stats.registrations.conversionRate = registrationStarts.size > 0 
      ? registrationCompletions.size / registrationStarts.size 
      : 0;

    // Fetch financial data from registrations collection (all-time, active registrations only)
    try {
      const registrationsRef = collection(db, 'registrations');
      const registrationsSnapshot = await getDocs(registrationsRef);
      
      let totalPaymentsMade = 0;
      let totalPaymentRequired = 0;
      let totalRefunds = 0;
      let paymentCount = 0;

      registrationsSnapshot.forEach(doc => {
        const reg = doc.data();
        
        // Skip cancelled/expired registrations
        if (reg.status === 'cancelled' || reg.status === 'expired') {
          return;
        }

        const paymentMade = reg.paymentMade || 0;
        const paymentRequired = reg.paymentRequired || 0;
        
        totalPaymentsMade += paymentMade;
        totalPaymentRequired += paymentRequired;
        
        if (paymentMade > 0) {
          paymentCount++;
        }

        // Check for refunds in payments array
        if (Array.isArray(reg.payments)) {
          reg.payments.forEach((payment: any) => {
            if (payment.type === 'refund' || payment.amount < 0) {
              totalRefunds += Math.abs(payment.amount || 0);
            }
          });
        }
      });

      stats.financial.totalIncome = totalPaymentsMade;
      stats.financial.paymentsMade = paymentCount;
      stats.financial.outstandingAmount = Math.max(0, totalPaymentRequired - totalPaymentsMade);
      stats.financial.refunds = totalRefunds;
    } catch (error) {
      console.warn('Failed to fetch financial data from registrations:', error);
      stats.financial.totalIncome = 0;
      stats.financial.paymentsMade = 0;
      stats.financial.outstandingAmount = 0;
      stats.financial.refunds = 0;
    }

    return stats;
  };

  // Fetch time-series data for charts
  // Uses raw events when available, falls back to daily aggregates for older data
  const fetchTimeSeriesData = async (startDate: Date, endDate: Date): Promise<Array<{ date: string; pageViews: number; sessions: number; registrations: number }>> => {
    const timeSeries: Array<{ date: string; pageViews: number; sessions: number; registrations: number }> = [];
    
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    for (let i = 0; i <= daysDiff; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      
      const dayStats = {
        date: dateStr,
        pageViews: 0,
        sessions: 0,
        registrations: 0
      };
      
      let hasRawData = false;
      
      try {
        const eventsCollectionRef = collection(db, 'analytics', 'events', dateStr);
        const eventsSnapshot = await getDocs(eventsCollectionRef);
        
        if (!eventsSnapshot.empty) {
          hasRawData = true;
          const sessions = new Set<string>();
          
          eventsSnapshot.forEach(doc => {
            const event = doc.data() as any;
            
            // Skip admin events if filter is enabled
            if (excludeAdmin && event.isAdmin === true) return;
            
            if (event.type === 'page_view') {
              dayStats.pageViews++;
            } else if (event.type === 'session_event' && event.data.action === 'session_start') {
              sessions.add(event.sessionId);
            } else if (event.type === 'registration_event' && event.data.action === 'registration_complete') {
              dayStats.registrations++;
            }
          });
          
          dayStats.sessions = sessions.size;
        }
      } catch (error) {
        // No raw data for this day
      }
      
      // Fall back to daily aggregate if no raw events found
      if (!hasRawData) {
        try {
          const aggRef = doc(db, 'analytics', 'aggregates', 'periods', `daily-${dateStr}`);
          const aggSnap = await getDoc(aggRef);
          if (aggSnap.exists()) {
            const agg = aggSnap.data() as any;
            dayStats.pageViews = excludeAdmin ? Math.max(0, (agg.pageViews || 0) - (agg.adminPageViews || 0)) : (agg.pageViews || 0);
            dayStats.sessions = excludeAdmin ? Math.max(0, (agg.sessions || 0) - (agg.adminSessions || 0)) : (agg.sessions || 0);
            dayStats.registrations = agg.registrationCompletions || 0;
          }
        } catch (error) {
          // No aggregate data either
        }
      }
      
      timeSeries.push(dayStats);
    }
    
    return timeSeries;
  };

  // Calculate hourly activity pattern (averaged over the selected period)
  const calculateHourlyActivity = async (startDate: Date, endDate: Date): Promise<Array<{ hour: number; pageViews: number; sessions: number }>> => {
    const hourlyStats = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      pageViews: 0,
      sessions: 0,
      count: 0 // Track number of days with data for averaging
    }));
    
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    for (let i = 0; i <= daysDiff; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      
      try {
        const eventsCollectionRef = collection(db, 'analytics', 'events', dateStr);
        const eventsSnapshot = await getDocs(eventsCollectionRef);
        
        if (!eventsSnapshot.empty) {
          const dailyHourlyStats = Array.from({ length: 24 }, () => ({
            pageViews: 0,
            sessions: new Set<string>()
          }));
          
          eventsSnapshot.forEach(doc => {
            const event = doc.data() as any;
            
            // Skip admin events if filter is enabled
            if (excludeAdmin && event.isAdmin === true) return;
            
            // Extract hour from timestamp
            const timestamp = event.timestamp?.toDate?.() || new Date(event.timestamp);
            const hour = timestamp.getHours();
            
            if (event.type === 'page_view') {
              dailyHourlyStats[hour].pageViews++;
            } else if (event.type === 'session_event' && event.data.action === 'session_start') {
              dailyHourlyStats[hour].sessions.add(event.sessionId);
            }
          });
          
          // Add this day's data to the totals
          dailyHourlyStats.forEach((stats, hour) => {
            if (stats.pageViews > 0 || stats.sessions.size > 0) {
              hourlyStats[hour].pageViews += stats.pageViews;
              hourlyStats[hour].sessions += stats.sessions.size;
              hourlyStats[hour].count++;
            }
          });
        }
      } catch (error) {
        // No data for this day
      }
    }
    
    // Calculate averages
    return hourlyStats.map(stat => ({
      hour: stat.hour,
      pageViews: stat.count > 0 ? Math.round(stat.pageViews / stat.count) : 0,
      sessions: stat.count > 0 ? Math.round(stat.sessions / stat.count) : 0
    }));
  };

  // Calculate daily activity pattern (averaged over the selected period)
  const calculateDailyActivity = async (startDate: Date, endDate: Date): Promise<Array<{ day: number; dayName: string; pageViews: number; sessions: number }>> => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dailyStats = Array.from({ length: 7 }, (_, day) => ({
      day,
      dayName: dayNames[day],
      pageViews: 0,
      sessions: 0,
      count: 0 // Track number of occurrences for averaging
    }));
    
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    for (let i = 0; i <= daysDiff; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
      
      try {
        const eventsCollectionRef = collection(db, 'analytics', 'events', dateStr);
        const eventsSnapshot = await getDocs(eventsCollectionRef);
        
        if (!eventsSnapshot.empty) {
          let dayPageViews = 0;
          const daySessions = new Set<string>();
          
          eventsSnapshot.forEach(doc => {
            const event = doc.data() as any;
            
            // Skip admin events if filter is enabled
            if (excludeAdmin && event.isAdmin === true) return;
            
            if (event.type === 'page_view') {
              dayPageViews++;
            } else if (event.type === 'session_event' && event.data.action === 'session_start') {
              daySessions.add(event.sessionId);
            }
          });
          
          dailyStats[dayOfWeek].pageViews += dayPageViews;
          dailyStats[dayOfWeek].sessions += daySessions.size;
          dailyStats[dayOfWeek].count++;
        }
      } catch (error) {
        // No data for this day
      }
    }
    
    // Calculate averages
    return dailyStats.map(stat => ({
      day: stat.day,
      dayName: stat.dayName,
      pageViews: stat.count > 0 ? Math.round(stat.pageViews / stat.count) : 0,
      sessions: stat.count > 0 ? Math.round(stat.sessions / stat.count) : 0
    }));
  };

  useEffect(() => {
    fetchStats(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, excludeAdmin]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats(dateRange);
    }, refreshInterval);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshInterval, dateRange]);

  const calculateTrend = (current: number, previous: number): number | undefined => {
    if (previous === 0 && current === 0) return undefined;
    if (previous === 0) return 100; // New activity where there was none
    return Math.round(((current - previous) / previous) * 100);
  };

  if (loading && !currentStats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!currentStats) {
    return (
      <Alert severity="info">
        No analytics data available for the selected period.
      </Alert>
    );
  }

  const pageViewsTrend = previousStats ? calculateTrend(currentStats.pageViews.total, previousStats.pageViews.total) : undefined;
  const sessionsTrend = previousStats ? calculateTrend(currentStats.sessions.total, previousStats.sessions.total) : undefined;
  const registrationsTrend = previousStats ? calculateTrend(currentStats.registrations.completed, previousStats.registrations.completed) : undefined;
  const revenueTrend = previousStats ? calculateTrend(currentStats.financial.totalIncome, previousStats.financial.totalIncome) : undefined;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          📊 Analytics Dashboard
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Refresh</InputLabel>
            <Select
              value={refreshInterval}
              label="Refresh"
              onChange={(e) => setRefreshInterval(e.target.value as number)}
              disabled={!autoRefresh}
            >
              <MenuItem value={10000}>10s</MenuItem>
              <MenuItem value={30000}>30s</MenuItem>
              <MenuItem value={60000}>1m</MenuItem>
              <MenuItem value={300000}>5m</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant={autoRefresh ? "contained" : "outlined"}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
            startIcon={autoRefresh ? <CircularProgress size={16} /> : <RefreshIcon />}
          >
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={dateRange}
              label="Period"
              onChange={(e) => setDateRange(e.target.value as number)}
            >
              <MenuItem value={1}>Last 24h</MenuItem>
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchStats(dateRange)}
            disabled={loading}
          >
            Refresh
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={excludeAdmin}
                onChange={(e) => setExcludeAdmin(e.target.checked)}
                size="small"
              />
            }
            label="Exclude admin"
          />
        </Box>
      </Box>

      {/* Status Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Last updated: {lastRefresh.toLocaleTimeString()}
          {autoRefresh && ` • Auto-refresh every ${refreshInterval/1000}s`}
        </Typography>
        {loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Updating...</Typography>
          </Box>
        )}
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Page Views"
            value={currentStats.pageViews.total.toLocaleString()}
            subtitle={`${currentStats.pageViews.unique} unique sessions`}
            trend={pageViewsTrend}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Sessions"
            value={currentStats.sessions.total.toLocaleString()}
            subtitle={`${Math.round(currentStats.sessions.averageDuration)}s avg duration • ${Math.round(currentStats.sessions.mobileShare * 100)}% mobile`}
            trend={sessionsTrend}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Registrations"
            value={currentStats.registrations.completed}
            subtitle={`${currentStats.registrations.started} started`}
            trend={registrationsTrend}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Revenue (all-time)"
            value={`${currentStats.financial.totalIncome.toLocaleString()} NOK`}
            subtitle={`${currentStats.financial.paymentsMade} payments • ${currentStats.financial.outstandingAmount.toLocaleString()} NOK outstanding`}
            trend={revenueTrend}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Time-Series Charts */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <TimeSeriesChart
            title="Page Views Over Time"
            data={timeSeriesData.map(d => ({ date: d.date, value: d.pageViews }))}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TimeSeriesChart
            title="Sessions Over Time"
            data={timeSeriesData.map(d => ({ date: d.date, value: d.sessions }))}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TimeSeriesChart
            title="Registrations Over Time"
            data={timeSeriesData.map(d => ({ date: d.date, value: d.registrations }))}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SimpleBarChart
            title="Top Pages by Views"
            data={currentStats.pageViews.topPages.slice(0, 5).map(page => ({
              label: page.page.replace(/^\/+/, ''), // Remove leading slashes
              value: page.views
            }))}
            height={200}
          />
        </Grid>
      </Grid>

      {/* Activity Patterns */}
      <Typography variant="h5" component="h2" sx={{ mb: 2, mt: 4 }}>
        📈 Activity Patterns
      </Typography>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <SimpleBarChart
                title="Hourly Activity (Averaged)"
                data={hourlyActivity.map(h => ({
                  label: `${h.hour}:00`,
                  value: h.pageViews
                }))}
                height={650}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <SimpleBarChart
                title="Hourly Sessions (Averaged)"
                data={hourlyActivity.map(h => ({
                  label: `${h.hour}:00`,
                  value: h.sessions
                }))}
                height={650}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <SimpleBarChart
                title="Weekly Activity - Page Views (Averaged)"
                data={dailyActivity.map(d => ({
                  label: d.dayName.substring(0, 3),
                  value: d.pageViews
                }))}
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <SimpleBarChart
                title="Weekly Activity - Sessions (Averaged)"
                data={dailyActivity.map(d => ({
                  label: d.dayName.substring(0, 3),
                  value: d.sessions
                }))}
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Pages */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Pages
          </Typography>
          {currentStats.pageViews.topPages.map((page, index) => (
            <Box key={page.page} display="flex" justifyContent="space-between" alignItems="center" py={1}>
              <Box>
                <Typography variant="body1">{page.page}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(page.avgTime / 60)}m {page.avgTime % 60}s avg time
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="body1" fontWeight="bold">
                  {page.views}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  views
                </Typography>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Registration by Event */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Registrations by Event
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            {Object.entries(currentStats.registrations.byEvent).map(([event, count]) => (
              <Chip
                key={event}
                label={`${event}: ${count}`}
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Conversion Rate: {Math.round(currentStats.registrations.conversionRate * 100)}%
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Session Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Session Stats
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bounce Rate: {Math.round(currentStats.sessions.bounceRate * 100)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mobile Share: {Math.round(currentStats.sessions.mobileShare * 100)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial Stats (all-time)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Income: {currentStats.financial.totalIncome.toLocaleString()} NOK
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Payments: {currentStats.financial.paymentsMade} registrations paid
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Outstanding: {currentStats.financial.outstandingAmount.toLocaleString()} NOK
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Refunds: {currentStats.financial.refunds.toLocaleString()} NOK
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Registration Funnel
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Started: {currentStats.registrations.started}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed: {currentStats.registrations.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsPanel;
